import { AxiosHeaders } from "axios";
import type { AxiosInstance, AxiosResponse } from "axios";
import type { ProductWorkspace } from "~/types/workspace";
import {
  shouldWaitForRefresh,
  waitForActiveRefresh,
  type AuthRequestConfig,
} from "~/repository/authSession";

const buildQueryString = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    queryParams.append(key, String(value));
  });

  return queryParams.toString();
};

class HttpFactory {
  protected readonly axios: AxiosInstance;

  constructor(
    axiosInstance: AxiosInstance,
    private workspace?: ProductWorkspace,
  ) {
    this.axios = axiosInstance;
  }

  /** A separate client keeps concurrent school and teacher requests isolated. */
  forWorkspace(workspace: ProductWorkspace): this {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this, {
      workspace,
    });
  }

  private requestConfig(extras: AuthRequestConfig): AuthRequestConfig {
    if (!this.workspace || extras.requiresAuth === false) return extras;
    const headers = new AxiosHeaders();
    for (const [name, value] of Object.entries(extras.headers || {})) {
      if (value !== undefined) headers.set(name, value);
    }
    headers.set("X-TeachPlus-Workspace", this.workspace);
    return { ...extras, headers: headers.toJSON() };
  }

  async get<T>(
    url: string,
    params: Record<string, string | number | boolean | undefined | null> = {},
    extras: AuthRequestConfig = {},
  ): Promise<T> {
    return await this.getResponse<T>(url, params, extras).then(
      (response) => response.data,
    );
  }

  protected async getResponse<T>(
    url: string,
    params: Record<string, string | number | boolean | undefined | null> = {},
    extras: AuthRequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    const queryString = buildQueryString(params);
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    if (shouldWaitForRefresh(extras)) {
      await waitForActiveRefresh();
    }

    return await this.axios.get<T>(fullUrl, this.requestConfig(extras));
  }

  async post<T>(
    url: string,
    data: unknown,
    extras: AuthRequestConfig = {},
  ): Promise<T> {
    if (shouldWaitForRefresh(extras)) {
      await waitForActiveRefresh();
    }

    return await this.axios
      .post<T>(url, data, this.requestConfig(extras))
      .then((response) => response.data);
  }

  async put<T>(
    url: string,
    data: unknown,
    extras: AuthRequestConfig = {},
  ): Promise<T> {
    if (shouldWaitForRefresh(extras)) {
      await waitForActiveRefresh();
    }

    return await this.axios
      .put<T>(url, data, this.requestConfig(extras))
      .then((response) => response.data);
  }

  async delete<T>(url: string, extras: AuthRequestConfig = {}): Promise<T> {
    if (shouldWaitForRefresh(extras)) {
      await waitForActiveRefresh();
    }

    return await this.axios
      .delete<T>(url, this.requestConfig(extras))
      .then((response) => response.data);
  }

  async patch<T>(
    url: string,
    data: unknown,
    extras: AuthRequestConfig = {},
  ): Promise<T> {
    if (shouldWaitForRefresh(extras)) {
      await waitForActiveRefresh();
    }

    return await this.axios
      .patch<T>(url, data, this.requestConfig(extras))
      .then((response) => response.data);
  }
}

export default HttpFactory;
